import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { AppModelModalComponent } from '@nimmsta/ng-commons-lib/forms';
import { AuthService } from '@nimmsta/ng-commons-lib/auth';
import { EmptyTableComponent } from '@nimmsta/ng-commons-lib/data-table';
import { HeaderBarComponent } from '@nimmsta/ng-commons-lib/header-bar';
import {
  Pageable,
  Software,
  SoftwareArtifact,
  UserRole,
} from '@nimmsta/ng-commons-lib/models';
import { SoftwareService } from '@nimmsta/ng-commons-lib/services/software';
import { fromEvent, debounceTime, distinctUntilChanged, merge } from 'rxjs';
import { SoftwareArtifactFormComponent } from '../software-artifact-form.component';
import { SoftwareArtifactService } from '../../services/software/software-artifact.service';
import { SoftwareDetailHeaderComponent } from './software-detail-header.component';
import { SoftwareArtifactComponent } from './software-artifact.component';

const COLLECTION_SIZE = 10;
@Component({
  templateUrl: './software-detail.component.html',
  styleUrls: ['./software-detail.component.scss', '../software-label.scss'],
  imports: [
    TranslatePipe,
    SoftwareDetailHeaderComponent,
    SoftwareArtifactComponent,
    SoftwareArtifactFormComponent,
    TranslatePipe,
    HeaderBarComponent,
    EmptyTableComponent,
    AppModelModalComponent,
  ],
})
export class SoftwareDetailComponent implements OnInit, AfterViewInit {
  private activatedRoute = inject(ActivatedRoute);
  private softwareService = inject(SoftwareService);
  private softwareArtifactService = inject(SoftwareArtifactService);
  private authService = inject(AuthService);
  private translate = inject(TranslateService);
  private ref = inject(ChangeDetectorRef);

  software?: Software;

  artifacts: SoftwareArtifact[] = [];

  latestArtifact?: SoftwareArtifact;

  showAllChangelogs = false;

  showCompactViewToggle = false;

  totalItems = 0;

  loadedItems = 0;

  currentPage = 0;

  softwareId?: string;

  isAdmin = false;

  loading = true;

  @ViewChild('EditModal', { static: true })
  private editModalComponent!: AppModelModalComponent<SoftwareArtifact>;

  @ViewChild(SoftwareArtifactFormComponent, { static: true })
  private softwareArtifactFormComponent!: SoftwareArtifactFormComponent;

  @ViewChild('box', { static: true })
  private boxReference!: ElementRef<HTMLDivElement>;

  @ViewChild('search', { static: true })
  private searchBox!: ElementRef;

  @ViewChild('searchMin', { static: true })
  private searchBoxMin!: ElementRef;

  searchValue = '';

  isScrollable = false;

  private boxResizeObserver = new ResizeObserver((entries) => {
    // check if we want to show the scroll down button
    const entry = entries[0];
    this.isScrollable = entry.contentRect.height > window.innerHeight;
    this.ref.detectChanges();
  });

  ngOnInit() {
    this.boxResizeObserver.observe(this.boxReference.nativeElement);
    this.authService.getCurrentUser().subscribe((user) => {
      this.isAdmin = user.role === UserRole.ADMIN;
    });
    this.activatedRoute.params.subscribe((params) => {
      this.softwareId = params['softwareId'];
      this.softwareArtifactService.useSoftwareId(this.softwareId!);
      this.softwareService.get(this.softwareId!).subscribe((software) => {
        this.software = software;
        this.latestArtifact = software.artifacts![0];
      });
      this.softwareArtifactService
        .list({
          sortColumn: 'createdAt',
          sortOrder: 'desc',
          pageNumber: this.currentPage,
          pageSize: COLLECTION_SIZE,
        })
        .subscribe((artifacts) => {
          this.processArtifacts(artifacts);
          this.loading = false;
        });
    });
  }

  ngAfterViewInit(): void {
    // Combine this.searchBox and this.searchBoxMin
    merge(
      fromEvent<Event>(this.searchBox.nativeElement, 'keyup'),
      fromEvent<Event>(this.searchBoxMin.nativeElement, 'keyup'),
    )
      .pipe(debounceTime(150), distinctUntilChanged())
      .subscribe({
        next: (e) => {
          this.currentPage = 0;
          this.searchValue = (e.target as HTMLInputElement).value ?? '';

          // Update search box values (in case of resize)
          this.searchBox.nativeElement.value = this.searchValue;
          this.searchBoxMin.nativeElement.value = this.searchValue;

          // Load data
          this.softwareArtifactService
            .list({
              search: this.searchValue,
              filter: [],
              sortColumn: 'createdAt',
              sortOrder: 'desc',
              pageNumber: this.currentPage,
              pageSize: COLLECTION_SIZE,
            })
            .subscribe((artifacts) => {
              this.totalItems = 0;
              this.loadedItems = 0;
              this.artifacts = [];
              this.processArtifacts(artifacts);
            });
        },
      });
  }

  loadMore() {
    if (this.loadedItems >= this.totalItems) {
      return;
    }
    this.currentPage++;
    this.softwareArtifactService
      .list({
        search: this.searchValue,
        sortColumn: 'createdAt',
        sortOrder: 'desc',
        pageNumber: this.currentPage,
        pageSize: COLLECTION_SIZE,
      })
      .subscribe((artifacts) => this.processArtifacts(artifacts));
  }

  private processArtifacts(artifactsPage: Pageable<SoftwareArtifact>) {
    this.showCompactViewToggle = false;
    this.totalItems = artifactsPage.totalElements;
    this.loadedItems += artifactsPage.content.length;
    const newArtifacts = artifactsPage.content;
    for (const item of newArtifacts) {
      if (item.changelog) {
        this.showCompactViewToggle = true;
        break;
      }
    }
    this.artifacts = this.artifacts.concat(newArtifacts);
  }

  scrollToVersions(): void {
    if (this.artifacts.length === 0) {
      return;
    }
    // Use native scrolling as scrolling to references loaded conditionally doesn't work with ViewportScroller
    const targetElement = document.getElementById('artifact_anchor')!;
    targetElement.scrollIntoView({ behavior: 'smooth' });
  }

  edit(id: string): void {
    this.translate.get('edit').subscribe((value) => {
      const artifact = this.getSelectedObject(id)!;
      this.openModal(value, true, artifact);
    });
  }

  getSelectedObject(id: string): SoftwareArtifact | undefined {
    return this.artifacts.find((artifact) => id === artifact.id);
  }

  openModal(
    title: string,
    editable: boolean,
    artifact: SoftwareArtifact,
  ): void {
    this.softwareArtifactFormComponent.editable = editable;
    this.softwareArtifactFormComponent.setValue(artifact);
    this.editModalComponent.title = title;
    this.editModalComponent.editable = editable;
    this.editModalComponent.show();
  }

  onSave(): void {
    this.softwareArtifactFormComponent.onSave();
  }

  onModalClose(): void {
    this.editModalComponent.hide();
    const artifactIndex = this.artifacts.findIndex(
      (item) => item.id === this.softwareArtifactFormComponent.value.id,
    );
    this.artifacts[artifactIndex] = this.softwareArtifactFormComponent.value;
    if (
      this.latestArtifact?.id === this.softwareArtifactFormComponent.value.id
    ) {
      this.latestArtifact = this.softwareArtifactFormComponent.value;
    }
  }

  onLoading(loading: boolean): void {
    this.editModalComponent.loading = loading;
  }
}
